// ============================================================================
// STAKEHOLDERS TAB
// Displays and manages human engagement stakeholders for a project —
// sponsors, sign-off authorities, consulted/informed parties.
// ============================================================================

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Users, Plus, Pencil, Trash2, Loader2, AlertTriangle, RefreshCw, ArrowUpDown, ChevronUp, ChevronDown } from 'lucide-react';
import { Stakeholder, StakeholderGovernanceLevel } from '../../types';
import { getStakeholders, deleteStakeholder } from '../../services/stakeholdersService';
import { AddEditStakeholderModal } from './AddEditStakeholderModal';

interface StakeholdersTabProps {
  projectId: string;
  token?: string;
}

type SortKey = 'name' | 'governanceLevel' | 'signOffOrder';
type SortDir = 'asc' | 'desc';

const GOVERNANCE_BADGE: Record<StakeholderGovernanceLevel, string> = {
  sponsor: 'bg-purple-50 text-purple-700 border border-purple-200',
  sign_off: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
  consulted: 'bg-blue-50 text-blue-700 border border-blue-200',
  informed: 'bg-slate-100 text-slate-500 border border-slate-200',
};

const GOVERNANCE_LABEL: Record<StakeholderGovernanceLevel, string> = {
  sponsor: 'Sponsor',
  sign_off: 'Sign-off',
  consulted: 'Consulted',
  informed: 'Informed',
};

const GOVERNANCE_ORDER: Record<StakeholderGovernanceLevel, number> = {
  sponsor: 0,
  sign_off: 1,
  consulted: 2,
  informed: 3,
};

function compareStakeholders(a: Stakeholder, b: Stakeholder, sortKey: SortKey): number {
  switch (sortKey) {
    case 'name': return a.name.localeCompare(b.name);
    case 'governanceLevel': return GOVERNANCE_ORDER[a.governanceLevel] - GOVERNANCE_ORDER[b.governanceLevel];
    case 'signOffOrder': return (a.signOffOrder ?? Infinity) - (b.signOffOrder ?? Infinity);
    default: return 0;
  }
}

function SortTh({
  label, sortK, currentKey, currentDir, onSort
}: {
  label: string; sortK: SortKey; currentKey: SortKey; currentDir: SortDir; onSort: (k: SortKey) => void;
}) {
  const active = currentKey === sortK;
  return (
    <th className="px-5 py-3 text-left cursor-pointer select-none group" onClick={() => onSort(sortK)}>
      <div className="flex items-center gap-1.5">
        <span className={`text-xs font-bold uppercase tracking-wider transition-colors ${active ? 'text-primary' : 'text-slate-400 group-hover:text-slate-600'}`}>
          {label}
        </span>
        {active
          ? (currentDir === 'asc' ? <ChevronUp size={12} className="text-primary" /> : <ChevronDown size={12} className="text-primary" />)
          : <ArrowUpDown size={12} className="text-slate-200 group-hover:text-slate-400 transition-colors" />}
      </div>
    </th>
  );
}

export const StakeholdersTab: React.FC<StakeholdersTabProps> = ({ projectId }) => {
  const [stakeholders, setStakeholders] = useState<Stakeholder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingStakeholder, setEditingStakeholder] = useState<Stakeholder | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('signOffOrder');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const loadStakeholders = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await getStakeholders(projectId);
      setStakeholders(result.stakeholders || []);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to load stakeholders');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadStakeholders();
  }, [loadStakeholders]);

  const handleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const handleCreated = (stakeholder: Stakeholder) => {
    setShowCreateModal(false);
    setStakeholders(prev => [stakeholder, ...prev]);
  };

  const handleSaved = (updated: Stakeholder) => {
    setEditingStakeholder(null);
    setStakeholders(prev => prev.map(s => s.id === updated.id ? updated : s));
  };

  const handleDelete = async (stakeholder: Stakeholder) => {
    if (!window.confirm(`Remove ${stakeholder.name} as a stakeholder?`)) return;
    setDeletingId(stakeholder.id);
    try {
      await deleteStakeholder(projectId, stakeholder.id);
      setStakeholders(prev => prev.filter(s => s.id !== stakeholder.id));
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to delete stakeholder');
    } finally {
      setDeletingId(null);
    }
  };

  const sortedStakeholders = useMemo(() => {
    return [...stakeholders].sort((a, b) => {
      const cmp = compareStakeholders(a, b, sortKey);
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [stakeholders, sortKey, sortDir]);

  return (
    <div className="flex flex-col gap-6">
      {/* Header row */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-bold text-navy">Stakeholders</h3>
          <p className="text-xs text-slate-500 mt-0.5">Sponsors, sign-off authorities, and consulted/informed parties for this engagement.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={loadStakeholders}
            disabled={loading}
            className="flex items-center gap-1.5 rounded-lg border border-[#e2e8f0] bg-white px-3 py-2 text-xs font-medium text-slate-600 hover:bg-slate-50 transition-colors disabled:opacity-50"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            Refresh
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center justify-center gap-2 rounded-xl h-9 px-4 bg-primary hover:bg-primary-dark transition-colors text-white text-xs font-bold shadow-sm"
          >
            <Plus size={14} /> New Stakeholder
          </button>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-3 px-4 py-3 rounded-xl border border-red-200 bg-red-50 text-red-700 text-sm">
          <AlertTriangle size={16} className="shrink-0" />
          {error}
        </div>
      )}

      <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
        {loading && stakeholders.length === 0 ? (
          <div className="flex justify-center items-center h-40">
            <Loader2 size={22} className="animate-spin text-slate-400" />
          </div>
        ) : !loading && stakeholders.length === 0 && !error ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center mb-4">
              <Users size={28} className="text-slate-400" />
            </div>
            <h4 className="text-sm font-semibold text-navy mb-1">No stakeholders yet</h4>
            <p className="text-sm text-slate-500 mb-5 max-w-xs">Track sponsors, sign-off authorities, and consulted/informed parties for this engagement.</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center justify-center gap-2 rounded-xl h-9 px-4 bg-primary hover:bg-primary-dark transition-colors text-white text-xs font-bold shadow-sm"
            >
              <Plus size={14} /> Add First Stakeholder
            </button>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#e2e8f0] bg-slate-50/50">
                <SortTh label="Name" sortK="name" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
                <th className="px-5 py-3 text-left"><span className="text-xs font-bold uppercase tracking-wider text-slate-400">Role</span></th>
                <SortTh label="Governance" sortK="governanceLevel" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
                <th className="px-5 py-3 text-left"><span className="text-xs font-bold uppercase tracking-wider text-slate-400">Area of Interest</span></th>
                <SortTh label="Sign-off Order" sortK="signOffOrder" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
                <th className="px-5 py-3 w-20" />
              </tr>
            </thead>
            <tbody className="divide-y divide-[#e2e8f0]">
              {sortedStakeholders.map(stakeholder => (
                <tr key={stakeholder.id} className="hover:bg-slate-50/60 transition-colors">
                  <td className="px-5 py-3.5">
                    <p className="text-sm font-semibold text-navy">{stakeholder.name}</p>
                  </td>
                  <td className="px-5 py-3.5">
                    <p className="text-sm text-slate-600">{stakeholder.role}</p>
                  </td>
                  <td className="px-5 py-3.5">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${GOVERNANCE_BADGE[stakeholder.governanceLevel]}`}>
                      {GOVERNANCE_LABEL[stakeholder.governanceLevel]}
                    </span>
                  </td>
                  <td className="px-5 py-3.5">
                    <p className="text-sm text-slate-500 truncate max-w-[260px]">{stakeholder.areaOfInterest || '—'}</p>
                  </td>
                  <td className="px-5 py-3.5">
                    <p className="text-sm text-slate-500">{stakeholder.signOffOrder ?? '—'}</p>
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center justify-end gap-3">
                      <button
                        onClick={() => setEditingStakeholder(stakeholder)}
                        className="p-1.5 rounded-lg text-slate-400 hover:text-navy hover:bg-slate-100 transition-colors"
                        title="Edit"
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        onClick={() => handleDelete(stakeholder)}
                        disabled={deletingId === stakeholder.id}
                        className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50"
                        title="Remove"
                      >
                        {deletingId === stakeholder.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showCreateModal && (
        <AddEditStakeholderModal
          projectId={projectId}
          onClose={() => setShowCreateModal(false)}
          onSaved={handleCreated}
        />
      )}

      {editingStakeholder && (
        <AddEditStakeholderModal
          projectId={projectId}
          stakeholder={editingStakeholder}
          onClose={() => setEditingStakeholder(null)}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
};

export default StakeholdersTab;
